import { useEffect, useState } from 'react'

/**
 * BuildPriceSlider — parallel to PriceSlider (shop filters). ONE
 * dual-handle range control over the same dark-walnut surface.
 */
interface Props {
  readonly catalogMinPaisa: number
  readonly catalogMaxPaisa: number
  readonly valueMinPaisa: number
  readonly valueMaxPaisa: number
  readonly onCommit: (next: { minPaisa: number; maxPaisa: number }) => void
  readonly disabled?: boolean
}

function rupeesLabel(paisa: number): string {
  return `₹${(paisa / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

export function BuildPriceSlider({
  catalogMinPaisa,
  catalogMaxPaisa,
  valueMinPaisa,
  valueMaxPaisa,
  onCommit,
  disabled = false
}: Props): JSX.Element {
  const [min, setMin] = useState<number>(valueMinPaisa)
  const [max, setMax] = useState<number>(valueMaxPaisa)

  useEffect(() => { setMin(valueMinPaisa) }, [valueMinPaisa])
  useEffect(() => { setMax(valueMaxPaisa) }, [valueMaxPaisa])

  const span = Math.max(1, catalogMaxPaisa - catalogMinPaisa)
  const step = Math.max(1, Math.floor(span / 100))
  const leftPct = Math.min(100, Math.max(0, ((min - catalogMinPaisa) / span) * 100))
  const rightPct = Math.min(100, Math.max(0, ((max - catalogMinPaisa) / span) * 100))

  function handleMinChange(raw: number): void {
    setMin(Math.min(raw, max))
  }
  function handleMaxChange(raw: number): void {
    setMax(Math.max(raw, min))
  }
  function commit(): void {
    onCommit({ minPaisa: min, maxPaisa: max })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between font-body text-body-xs text-text-light/70">
        <span className="font-mono text-text-light">{rupeesLabel(min)}</span>
        <span className="font-mono text-text-light">{rupeesLabel(max)}</span>
      </div>
      <div className="dual-range relative h-5">
        <div
          aria-hidden
          className="absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-text-light/20"
        />
        <div
          aria-hidden
          className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-forest"
          style={{ left: `${leftPct}%`, right: `${100 - rightPct}%` }}
        />
        <input
          type="range"
          min={catalogMinPaisa}
          max={catalogMaxPaisa}
          step={step}
          value={min}
          disabled={disabled}
          onChange={(e) => handleMinChange(Number(e.target.value))}
          onMouseUp={commit}
          onTouchEnd={commit}
          onKeyUp={commit}
          aria-label="Minimum price"
          className="absolute inset-0 z-[3] w-full appearance-none bg-transparent accent-forest"
        />
        <input
          type="range"
          min={catalogMinPaisa}
          max={catalogMaxPaisa}
          step={step}
          value={max}
          disabled={disabled}
          onChange={(e) => handleMaxChange(Number(e.target.value))}
          onMouseUp={commit}
          onTouchEnd={commit}
          onKeyUp={commit}
          aria-label="Maximum price"
          className="absolute inset-0 z-[4] w-full appearance-none bg-transparent accent-forest"
        />
      </div>
      <p className="font-body text-body-xs text-text-light/70">
        Range {rupeesLabel(catalogMinPaisa)} – {rupeesLabel(catalogMaxPaisa)}
      </p>
      <style>{`
        .dual-range input[type='range'] { pointer-events: none; }
        .dual-range input[type='range']::-webkit-slider-runnable-track,
        .dual-range input[type='range']::-moz-range-track { background: transparent; height: 0; }
        .dual-range input[type='range']::-webkit-slider-thumb {
          pointer-events: auto;
          -webkit-appearance: none; appearance: none;
          width: 16px; height: 16px; border-radius: 9999px;
          background: #4F6F52; border: 2px solid #FAF5ED;
          box-shadow: 0 1px 2px rgba(0,0,0,0.15);
          cursor: pointer;
        }
        .dual-range input[type='range']::-moz-range-thumb {
          pointer-events: auto;
          width: 16px; height: 16px; border-radius: 9999px;
          background: #4F6F52; border: 2px solid #FAF5ED;
          box-shadow: 0 1px 2px rgba(0,0,0,0.15);
          cursor: pointer;
        }
      `}</style>
    </div>
  )
}
